<template>
  <b-modal :id="modal_id" size="lg"
           :title="$t('system.utils.update_template', {object: $t('system.account')})"
           @hidden="resetAccountData" v-if="account">
    <b-form>
      <b-form-group :label="$t('system.description') + ':'" label-for="input-account-edit-description">
        <b-form-textarea
          id="input-account-edit-description"
          v-model.trim="account_description"
          :placeholder="$t('views.netdb.tokens.enter_description')"
        />
      </b-form-group>
      <b-form-group v-if="account.is_svc" :label="$t('components.svc_card.svc_id') + ':'" label-for="input-account-edit-svc-id">
        <b-form-input
          id="input-account-edit-svc-id"
          v-model.trim="account_svc_id"
        />
      </b-form-group>
      <b-form-group v-if="(account.is_svc || account.is_sub) && mgr_object_definition != null">
        <b-form-checkbox v-model="account_allow_data_manipulation" switch>
          {{ $t_netdb(mgr_object_definition.attributes.allow_data_manipulation.description_global_scope) }}
        </b-form-checkbox>
      </b-form-group>
      <template
        v-if="$store.state.user != null && account_roles != null && effective_assignable_role_name_options_by_system != null">
        <b-form-group :label="$t('views.netdb.tokens.roles')">
          <b-input-group v-if="can_edit_roles">
            <b-form-select @change="updateCanAddRole" v-model="new_system"
                           :options="effective_assignable_system_options"/>
            <b-form-select @change="updateCanAddRole" v-model="new_role_name"
                           :options="effective_assignable_role_name_options_by_system[new_system]"/>
            <b-input-group-append>
              <b-button variant="success" :disabled="!can_add_role" @click="addRole">
                <template v-if="cant_add_role_reason === ''">{{ $t('views.netdb.tokens.add_role') }}</template>
                <span v-else class="text-danger">{{ cant_add_role_reason }}</span>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <h5 class="my-1">
              <span
                :key="'role-badge-' + role.mgr_login_name + '-' + role.fq_name"
                v-for="role in account_roles">
                <b-button
                  :id="'create-account-role-badge-' + role.fq_name"
                  :style="{background: role.fq_name.toHSL({lit: [30, 40]}), border: 'none'}"
                  class="mr-1 mb-1 badge" tabindex="0" href="#">
                  <div class="d-flex align-items-center">
                    {{ role.system.toUpperCase() }}<br>{{ role.name }}
                    <span v-if="can_edit_roles" @click="removeRole(role)" class="p-2">
                      <netvs-icon style="opacity: 0.7" icon="clear"></netvs-icon>
                    </span>
                  </div>
                </b-button>
                <b-popover
                  :key="'role-badge-tooltip' + role.mgr_login_name + '-' + role.fq_name"
                  :target="'create-account-role-badge-' + role.fq_name"
                  triggers="focus" custom-class="popover-wide"
                  placement="bottom">
                  <b-table :fields="permission_list_fields"
                           :items="Object.values(permissions).filter(p => (p.hierarchy_gfk_list || []).includes(role.gpk))"
                           sticky-header/>
                </b-popover>
              </span>
            <b-button v-if="can_edit_roles && account_roles.length > 0" variant="outline-danger" block class="mt-1"
                      @click="removeAllRoles">
              {{ $t('views.netdb.tokens.remove_all_roles') }}
            </b-button>
          </h5>
        </b-form-group>
      </template>
    </b-form>
    <template v-slot:modal-footer="{cancel}">
      <b-alert id="alert-edit-account" v-model="show_modal_alert"
               variant="danger" dismissible fade class="mb-0 flex-grow">
        {{ modal_alert_content }}
      </b-alert>
      <b-button variant="outline-secondary" @click="cancel">
        {{ $t('system.cancel') }}
      </b-button>
      <b-button variant="primary" :disabled="waiting" @click="edit_account">
        {{ $t('views.netdb.tokens.save_changes') }}
        <b-spinner small v-show="waiting"/>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {v4 as uuidv4} from 'uuid'
import TransactionUtil from '@/util/transactionutil'
import TransactionService from '@/api-services/transaction.service'
import CntlMgr2role from '@/api-services.gen/cntl.mgr2role'

export default {
  name: 'MgrEditor',
  methods: {
    resetAccountData() {
      this.new_system = ''
      this.new_role_name = ''
    },
    async fetchParentRoles(parent_login_name) {
      const ta = [
        { name: 'cntl.mgr2role.list', idx: 'parent_mgr2role', old: { mgr_login_name_list: [parent_login_name] } },
        { name: 'cntl.role.list', idx: 'parent_roles', inner_join_ref: { parent_mgr2role: 'default' }, old: { is_role: true, sorting_params_list: ['position'] } },
        { name: 'cntl.role.list', idx: 'contained', inner_join_ref: { parent_roles: 'api_func_cntl_role_hierarchy_gfk_list_is_superset' }, old: { is_role: true, sorting_params_list: ['position'] } }
      ]
      try {
        const res = await TransactionService.execute(this.$store.state, ta)
        if (this.account == null || this.account.parent_login_name !== parent_login_name) {
          return
        }
        const allowed = new Set(res.data.parent_roles.map(r => r.fq_name))
        res.data.contained.forEach(r => allowed.add(r.fq_name))
        this.parent_allowed_fq_names = Array.from(allowed)
      } catch (e) {
        console.error(e)
        this.parent_allowed_fq_names = []
      }
    },
    addRole(e) {
      e.preventDefault()
      const new_role = this.new_system + '.' + this.new_role_name
      this.account_roles.push(this.effective_assignable_roles_by_fq_name[new_role][0])
      this.updateCanAddRole()
    },
    removeRole(role) {
      const index = this.account_roles.indexOf(role)
      if (index > -1) {
        this.account_roles.splice(index, 1)
      }
      this.updateCanAddRole()
    },
    removeAllRoles() {
      this.account_roles = []
      this.updateCanAddRole()
    },
    updateCanAddRole() {
      const new_role = this.new_system + '.' + this.new_role_name
      if (!this.new_system || !this.new_role_name || !this.effective_assignable_roles_by_fq_name[new_role]) {
        this.can_add_role = false
        this.cant_add_role_reason = this.$t('views.netdb.tokens.select_system_role')
        return
      }
      let valid = true
      this.account_roles.forEach(role => {
        if (role.fq_name === new_role) {
          valid = false
          this.cant_add_role_reason = this.$t('views.netdb.tokens.role_already_added')
        } else if ((this.subroles[new_role]?.hierarchy_gfk_list || []).includes(role.gpk)) {
          valid = false
          this.cant_add_role_reason = role.fq_name + ' ' + this.$t('views.netdb.tokens.contains') + ' ' + new_role
        }
      })

      this.can_add_role = valid
      if (valid) {
        this.cant_add_role_reason = ''
      }
    },
    async edit_account() {
      this.waiting = true
      try {
        const old_roles = []
        if (this.mgr_roles) {
          this.mgr_roles.forEach(role => {
            old_roles.push(role.fq_name)
          })
        }
        const new_roles = []
        this.account_roles.forEach(role => {
          new_roles.push(role.fq_name)
        })
        const roles_to_add = new_roles.filter(x => !old_roles.includes(x))
        const roles_to_remove = old_roles.filter(x => !new_roles.includes(x))
        const update_fields = {}
        if (String(this.account?.description ?? '') !== String(this.account_description)) {
          update_fields.description = this.account_description
        }
        if (this.account?.is_svc && String(this.account?.svc_id ?? '') !== String(this.account_svc_id)) {
          update_fields.svc_id = this.account_svc_id
        }
        if (Boolean(this.account?.allow_data_manipulation) !== Boolean(this.account_allow_data_manipulation)) {
          update_fields.allow_data_manipulation = this.account_allow_data_manipulation
        }
        if (Object.keys(update_fields).length > 0) {
          const mgr_obj = await TransactionUtil.getObjectMetadata(this.$store.state, this.$store.commit, 'cntl.mgr')
          const ta = {
            object_fq_name: 'cntl.mgr',
            object_function: 'update',
            show_result: false,
            input_reducer: {},
            non_optionals_order: ['login_name', 'sub_id', 'svc_id', 'kit_status', 'description', 'is_adm_locked', 'allow_data_manipulation'],
            non_optionals: [],
            func_descr: mgr_obj.object_functions.update.parameters,
            object_title: this.account.login_name,
            object_functions: mgr_obj.object_functions,
            preview_fields: Object.keys(update_fields),
            parameters: {
              new: update_fields,
              old: { login_name: this.account.login_name }
            }
          }
          const wrapper_ta = {
            content: ta,
            variant: TransactionUtil.function2variant('update'),
            old_uuid: null,
            uuid: uuidv4(),
            ta_type: 'db_editor_simple'
          }
          this.$store.commit('addTransactionElement', wrapper_ta)
        }
        const mgr2role_obj = await TransactionUtil.getObjectMetadata(this.$store.state, this.$store.commit, 'cntl.mgr2role')
        for (const role of roles_to_add) {
          const ta = {
            object_fq_name: 'cntl.mgr2role',
            object_function: 'create',
            show_result: false,
            input_reducer: {},
            non_optionals_order: ['mgr_login_name', 'role_fq_name'],
            non_optionals: [],
            func_descr: mgr2role_obj.object_definition,
            object_title: `${role} for ${this.account.login_name}`,
            object_functions: mgr2role_obj.object_functions,
            preview_fields: [],
            parameters: {
              new: {
                mgr_login_name: this.account.login_name,
                role_fq_name: role
              }
            }
          }
          const wrapper_ta = {
            content: ta,
            variant: TransactionUtil.function2variant('create'),
            old_uuid: null,
            uuid: uuidv4(),
            ta_type: 'db_editor_simple'
          }
          this.$store.commit('addTransactionElement', wrapper_ta)
        }
        for (const role of roles_to_remove) {
          const obj = {
            mgr_login_name: this.account.login_name,
            role_fq_name: role
          }
          this.$store.commit('addTransactionElement', TransactionUtil.generateDeleteElement('cntl.mgr2role', CntlMgr2role.deleteParamsList(), obj, `${role} for ${this.account.login_name}`))
        }
        this.$bvModal.hide(this.modal_id)
      } finally {
        this.waiting = false
      }
    },
  },
  computed: {
    effective_allowed_set() {
      if (this.account == null || !this.account.is_sub) {
        return null
      }
      if (this.parent_allowed_fq_names == null) {
        return new Set()
      }
      return new Set(this.parent_allowed_fq_names)
    },
    effective_assignable_roles_by_fq_name() {
      const allowed = this.effective_allowed_set
      if (allowed == null) {
        return this.assignable_roles_by_fq_name
      }
      const out = {}
      Object.keys(this.assignable_roles_by_fq_name).forEach(fq => {
        if (allowed.has(fq)) {
          out[fq] = this.assignable_roles_by_fq_name[fq]
        }
      })
      return out
    },
    effective_assignable_role_name_options_by_system() {
      const allowed = this.effective_allowed_set
      if (allowed == null) {
        return this.assignable_role_name_options_by_system
      }
      const out = {}
      Object.keys(this.assignable_role_name_options_by_system).forEach(system => {
        out[system] = this.assignable_role_name_options_by_system[system].filter(option => allowed.has(system + '.' + option.value))
      })
      return out
    },
    effective_assignable_system_options() {
      const by_system = this.effective_assignable_role_name_options_by_system
      return this.assignable_system_options.filter(option => (by_system[option.value] || []).length > 0)
    },
    permission_list_fields() {
      return [
        {
          key: 'system',
          label: this.$t('views.netdb.tokens.system')
        },
        {
          key: 'name',
          label: this.$t('views.netdb.tokens.permission')
        }
      ]
    },
  },
  data() {
    return {
      can_add_role: false,
      cant_add_role_reason: '',
      modal_alert_content: '',
      show_modal_alert: false,
      new_system: '',
      new_role_name: '',
      account_description: '',
      account_svc_id: '',
      account_allow_data_manipulation: true,
      account_roles: [],
      mgr_object_definition: null,
      parent_allowed_fq_names: null,
      waiting: false,
    }
  },
  watch: {
    mgr_roles: {
      immediate: true,
      handler(val) {
        this.account_roles = val.slice()
      }
    },
    account: {
      immediate: true,
      handler(val) {
        this.account_description = val?.description ?? ''
        this.account_svc_id = val?.svc_id ?? ''
        this.account_allow_data_manipulation = val?.allow_data_manipulation ?? false
        if (val != null && val.is_sub) {
          this.parent_allowed_fq_names = null
          this.fetchParentRoles(val.parent_login_name)
        } else {
          this.parent_allowed_fq_names = null
        }
      }
    }
  },
  async created() {
    const mgr_obj = await TransactionUtil.getObjectMetadata(this.$store.state, this.$store.commit, 'cntl.mgr')
    if (mgr_obj != null) {
      this.mgr_object_definition = mgr_obj.object_definition
    }
  },
  props: {
    account: {
      type: Object,
      required: false,
    },
    modal_id: {
      type: String,
      required: true,
    },
    mgr_roles: {
      type: Array,
      required: false,
      default: () => []
    },
    permissions: {
      type: Object,
      required: true,
    },
    assignable_system_options: {
      type: Array,
      required: true,
    },
    assignable_role_name_options_by_system: {
      type: Object,
      required: true,
    },
    assignable_roles_by_fq_name: {
      type: Object,
      required: true,
    },
    subroles: {
      type: Object,
      required: true,
    },
    can_edit_roles: {
      type: Boolean,
      default() {
        return false
      }
    }
  }
}
</script>

<style scoped>

</style>
